import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Heart, Shield, Clock, CheckCircle, Star, Quote, ChevronLeft, ChevronRight, Sparkles, Target, Microscope, Users, Award } from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Landing = () => {
  const [packages, setPackages] = useState([]);
  const [recensioni, setRecensioni] = useState([]);
  const [currentRecensioneIndex, setCurrentRecensioneIndex] = useState(0);

  useEffect(() => {
    loadPackages();
    loadRecensioni();
  }, []);

  // Auto-rotate reviews every 5 seconds
  useEffect(() => {
    if (recensioni.length === 0) return;
    const interval = setInterval(() => {
      setCurrentRecensioneIndex(prev => (prev + 1) % recensioni.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [recensioni.length]);

  const loadPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Error loading packages:', error);
    }
  };

  const loadRecensioni = async () => {
    try {
      const response = await axios.get(`${API}/reviews/patient`);
      // Mappa i campi dal nuovo formato
      const mappedReviews = response.data.map(r => ({
        nome: r.user_initials,
        testo: r.content,
        rating: r.rating,
        title: r.title,
        specializzazione: null,
        is_verified_user: r.is_verified_user !== false, // Default true
        is_subscribed_user: r.is_subscribed_user === true // Paziente abbonato
      }));
      setRecensioni(mappedReviews);
    } catch (error) {
      console.error('Error loading reviews:', error);
      // Fallback a recensioni statiche se API non disponibile
      setRecensioni([
        { nome: "M.R.", testo: "Professionalità e competenza eccezionali. Mi sono sentito subito in buone mani.", rating: 4, is_verified_user: true, is_subscribed_user: true },
        { nome: "L.B.", testo: "Finalmente un centro dove la prevenzione viene prima di tutto. Consigliatissimo!", rating: 4, is_verified_user: true, is_subscribed_user: false },
        { nome: "A.G.", testo: "Tecnologie all'avanguardia e personale gentilissimo. Esperienza molto positiva.", rating: 4, is_verified_user: true, is_subscribed_user: true }
      ]);
    }
  };

  const nextRecensione = () => {
    setCurrentRecensioneIndex(prev => (prev + 1) % recensioni.length);
  };

  const prevRecensione = () => {
    setCurrentRecensioneIndex(prev => (prev - 1 + recensioni.length) % recensioni.length);
  };

  const services = [
    {
      id: 'refractive_errors',
      title: 'Vizi Refrattivi',
      titleColor: '#2A9FCB',
      description: 'Miopia, ipermetropia, astigmatismo e presbiopia. Diagnosi e trattamento completo.',
      image: '/api/static/treatments/vizi_refrattivi.png',
      icon: Eye
    },
    {
      id: 'strabismus',
      title: 'Strabismo',
      titleColor: '#FA7314',
      description: 'Valutazione e correzione dello strabismo in bambini e adulti.',
      image: '/api/static/treatments/strabismo.png',
      icon: Eye
    },
    {
      id: 'refractive_surgery',
      title: 'Chirurgia Refrattiva',
      titleColor: '#2c4667',
      description: 'Laser e tecniche avanzate per correggere i difetti visivi permanentemente.',
      image: 'https://customer-assets.emergentagent.com/job_0d66167d-5cb1-4a21-a474-b2a61f30c4f4/artifacts/9l8woyes_image.png',
      icon: Shield
    },
    {
      id: 'cataract',
      title: 'Chirurgia della Cataratta',
      titleColor: '#1E6CA6',
      description: 'Intervento di facoemulsificazione con impianto di cristallino artificiale premium.',
      image: '/api/static/treatments/cataratta_new.png',
      icon: Shield
    },
    {
      id: 'dry_eye',
      title: 'Occhio Secco',
      titleColor: '#D4A017',
      description: 'Diagnosi e trattamento dell\'occhio secco con terapie innovative.',
      image: 'https://images.unsplash.com/photo-1631042000681-8638445d15ef?w=600&q=80',
      icon: Clock
    },
    {
      id: 'glaucoma',
      title: 'Glaucoma',
      titleColor: '#2E8B57',
      description: 'Diagnosi precoce e gestione del glaucoma per preservare la vista.',
      image: 'https://customer-assets.emergentagent.com/job_0d66167d-5cb1-4a21-a474-b2a61f30c4f4/artifacts/05r4jf05_image.png',
      icon: Eye
    },
    {
      id: 'keratoconus',
      title: 'Cheratocono',
      titleColor: '#8B5CF6',
      description: 'Trattamenti avanzati per cheratocono: cross-linking, lenti speciali e trapianto.',
      image: 'https://customer-assets.emergentagent.com/job_0d66167d-5cb1-4a21-a474-b2a61f30c4f4/artifacts/9gz05ecj_image.png',
      icon: Shield
    },
    {
      id: 'maculopathy',
      title: 'Maculopatia',
      titleColor: '#DC2626',
      description: 'Diagnosi e trattamento delle patologie maculari (DMLE, pucker, foro maculare).',
      image: '/api/static/treatments/maculopatia.png',
      icon: Heart
    },
    {
      id: 'amblyopia',
      title: 'Ambliopia (Occhio Pigro)',
      titleColor: '#0891B2',
      description: 'Diagnosi precoce e trattamento dell\'ambliopia nei bambini per prevenire deficit visivi permanenti.',
      image: '/images/ambliopia.jpg',
      icon: Eye
    },
    {
      id: 'hypermetropia',
      title: 'Ipermetropia',
      titleColor: '#EAB308',
      description: 'Correzione dell\'ipermetropia con occhiali, lenti a contatto o chirurgia refrattiva.',
      image: '/api/static/treatments/ipermetropia.png',
      icon: Eye
    },
    {
      id: 'presbyopia',
      title: 'Presbiopia',
      titleColor: '#9333EA',
      description: 'Soluzioni per la presbiopia: occhiali progressivi, lenti multifocali e chirurgia.',
      image: '/api/static/treatments/presbiopia_new.png',
      icon: Clock
    },
    {
      id: 'astigmatism',
      title: 'Astigmatismo',
      titleColor: '#16A34A',
      description: 'Correzione dell\'astigmatismo con lenti toriche o interventi laser personalizzati.',
      image: 'https://images.unsplash.com/photo-1653491626438-0cc265644b65?w=600&q=80',
      icon: Eye
    },
    {
      id: 'pediatric_vision',
      title: 'Visita Pediatrica',
      titleColor: '#F59E0B',
      description: 'Screening visivo per bambini: prima visita entro i 3 anni, controllo pre-scolare, prevenzione miopia.',
      image: '/api/static/treatments/visita_pediatrica.png',
      icon: Eye
    },
    {
      id: 'chalazion',
      title: 'Calazio e Orzaiolo',
      titleColor: '#EA580C',
      description: 'Diagnosi differenziale e trattamento delle cisti palpebrali: impacchi, cortisone e chirurgia.',
      image: '/api/static/treatments/calazio_orzaiolo.png',
      icon: Eye
    },
    {
      id: 'blepharoplasty',
      title: 'Blefaroplastica',
      titleColor: '#7C3AED',
      description: 'Chirurgia estetica delle palpebre: ringiovanisci il tuo sguardo con interventi mini-invasivi.',
      image: '/api/static/treatments/blefaroplastica.png',
      icon: Sparkles
    },
    {
      id: 'miopia',
      title: 'Miopia',
      titleColor: '#2A9FCB',
      description: 'Diagnosi e correzione della miopia: occhiali, lenti a contatto, ortocheratologia e chirurgia refrattiva laser.',
      image: '/api/static/treatments/miopia.png',
      icon: Eye
    },
    {
      id: 'pterygium',
      title: 'Pterigio',
      titleColor: '#059669',
      description: 'Diagnosi e trattamento chirurgico dello pterigio con tecniche mini-invasive e basso rischio di recidiva.',
      image: '/api/static/treatments/pterigio.png',
      icon: Eye
    },
    {
      id: 'conjunctivitis',
      title: 'Congiuntiviti',
      titleColor: '#E11D48',
      description: 'Diagnosi e trattamento delle congiuntiviti allergiche, batteriche e virali.',
      image: 'https://images.unsplash.com/photo-1614226355179-b31ece3e8cc1?w=600&q=80',
      icon: Eye
    },
    {
      id: 'keratitis',
      title: 'Cheratiti Infettive',
      titleColor: '#B91C1C',
      description: 'Diagnosi e trattamento urgente delle infezioni corneali: Herpes, Acanthamoeba, batteriche e micotiche.',
      image: 'https://images.unsplash.com/photo-1592869642456-7fee88e27aad?w=600&q=80',
      icon: Shield
    },
    {
      id: 'diabetic_retinopathy',
      title: 'Retinopatia Diabetica',
      titleColor: '#7C3AED',
      description: 'Screening, diagnosi e trattamento delle complicanze oculari del diabete. Fondo oculare e iniezioni anti-VEGF.',
      image: 'https://customer-assets.emergentagent.com/job_0d66167d-5cb1-4a21-a474-b2a61f30c4f4/artifacts/6y4s2ghf_image.png',
      icon: Heart
    },
    {
      id: 'ptosis',
      title: 'Ptosi Palpebrale',
      titleColor: '#6D28D9',
      description: 'Diagnosi e trattamento chirurgico della palpebra cadente, congenita o acquisita.',
      image: 'https://customer-assets.emergentagent.com/job_0d66167d-5cb1-4a21-a474-b2a61f30c4f4/artifacts/ui4xcgax_image.png',
      icon: Eye
    },
    {
      id: 'tecnologia',
      title: 'Tecnologia al Servizio dei Tuoi Occhi',
      titleColor: '#475569',
      description: 'Strumentazione diagnostica all\'avanguardia: OCT, Retinografia, Topografia Corneale, Autorefrattometro, Tonometro, Conta Endoteliale, Campo Visivo Computerizzato, Ecografo Bulbare.',
      image: '/api/static/treatments/tecnologia_vista.png',
      icon: Shield,
      customLink: '/tecnologie'
    }
  ];

  // Counter animation hook - solo per la percentuale soddisfazione
  const [soddisfazione, setSoddisfazione] = useState(0);
  const [countersStarted, setCountersStarted] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById('hero-counters');
      if (heroSection && !countersStarted) {
        const rect = heroSection.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          setCountersStarted(true);
          // Animate only the percentage counter
          const target = 100;
          const duration = 2000;
          const steps = 60;
          const interval = duration / steps;
          
          let step = 0;
          const timer = setInterval(() => {
            step++;
            const progress = step / steps;
            const easeOut = 1 - Math.pow(1 - progress, 3);
            setSoddisfazione(Math.round(target * easeOut));
            if (step >= steps) clearInterval(timer);
          }, interval);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    return () => window.removeEventListener('scroll', handleScroll);
  }, [countersStarted]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section - Luxury Style with Animations */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Image with Parallax Effect */}
        <div className="absolute inset-0 z-0">
          <div 
            className="w-full h-[120%] -mt-[10%] transform scale-105"
            style={{
              backgroundImage: 'url(/images/surgery/microscope.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundAttachment: 'fixed'
            }}
          />
          <div className="absolute inset-0" style={{background: 'linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.88) 35%, rgba(255,255,255,0.6) 55%, rgba(255,255,255,0.2) 75%, rgba(255,255,255,0.05) 100%)'}} />
          {/* Top-to-bottom white gradient - stronger on top 1/3, lighter on bottom 2/3 */}
          <div className="absolute inset-0" style={{background: 'linear-gradient(to bottom, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.7) 20%, rgba(255,255,255,0.35) 33%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 75%, rgba(255,255,255,0.05) 100%)'}} />
          {/* Decorative Elements */}
          <div className="absolute top-20 right-20 w-72 h-72 bg-[#2A9FCB]/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-40 right-40 w-96 h-96 bg-[#1e7ba8]/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-44 md:pt-52 pb-20 md:pb-24 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-2xl">
              <p 
                className="text-sm font-medium tracking-[0.3em] uppercase mb-6 opacity-0 animate-fade-in-up"
                style={{ color: '#2A9FCB', animationDelay: '0.1s', animationFillMode: 'forwards' }}
              >
                Centro Oftalmologico d'Eccellenza
              </p>
              <h1 
                className="text-5xl md:text-7xl font-normal mb-8 leading-[1.1] tracking-tight opacity-0 animate-fade-in-up"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  color: '#2c4667',
                  animationDelay: '0.2s',
                  animationFillMode: 'forwards'
                }}
              >
                La tua vista<br />
                <span className="text-[#2A9FCB]">merita il meglio</span>
              </h1>
              <p 
                className="text-lg md:text-xl mb-10 leading-relaxed opacity-0 animate-fade-in-up"
                style={{ 
                  color: '#2c4667',
                  animationDelay: '0.3s',
                  animationFillMode: 'forwards',
                  maxWidth: '500px'
                }}
              >
                Un approccio innovativo alla salute visiva. 
                <strong className="text-[#2a9fcb]"> Prevenzione</strong>, 
                <strong className="text-[#2a9fcb]"> cura personalizzata</strong> e 
                <strong className="text-[#2a9fcb]"> tecnologie all'avanguardia</strong>.
              </p>
              <div 
                className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up"
                style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
              >
                <Link 
                  to="/prenota" 
                  className="group inline-flex items-center justify-center bg-[#FA7314] text-white hover:bg-[#e0650f] hover:text-white rounded-xl px-10 py-4 text-sm font-medium transition-all duration-500 shadow-lg hover:shadow-2xl hover:-translate-y-1 hover:scale-105"
                  data-testid="hero-book-btn"
                  style={{ color: 'white' }}
                >
                  <span className="text-white">Prenota una Visita</span>
                  <ChevronRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/tests" 
                  className="group inline-flex items-center justify-center bg-white/80 backdrop-blur-sm border-2 border-[#2A9FCB] text-[#2A9FCB] hover:bg-[#2A9FCB] hover:text-white rounded-xl px-10 py-4 text-sm font-medium transition-all duration-500"
                  data-testid="hero-tests-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Test Gratuito Online
                </Link>
              </div>
            </div>
            
            {/* Hero Stats - Right Side */}
            <div id="hero-counters" className="hidden lg:block opacity-0 animate-fade-in-up" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: '48-72h', label: 'Appuntamento Disponibile', icon: Clock, isText: true },
                  { value: '360°', label: 'Percorso Completo', icon: Eye, isText: true },
                  { value: 'Sempre', label: 'Follow-up e Assistenza', icon: Heart, isText: true },
                  { value: soddisfazione, suffix: '%', label: 'Pazienti Soddisfatti', icon: Star, isText: false }
                ].map((stat, idx) => (
                  <div 
                    key={idx}
                    className="group bg-white/80 backdrop-blur-md p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100/50 hover:-translate-y-2"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2A9FCB] to-[#1e7ba8] flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                        <stat.icon className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-[#2c4667]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                      {stat.isText ? stat.value : `${stat.value}${stat.suffix || ''}`}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-[#2A9FCB]/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-[#2A9FCB] rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Perché Sceglierci - Section - Enhanced */}
      <section className="py-20 md:py-28 px-6 md:px-12 relative overflow-hidden" style={{background: 'linear-gradient(180deg, #2c4667 0%, #1a2d42 100%)'}} data-testid="perche-sceglierci-section">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#2A9FCB]/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#1e7ba8]/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-white/5 to-transparent rounded-full" />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <p 
              className="inline-block text-sm font-medium tracking-[0.3em] uppercase mb-4 px-4 py-2 rounded-full bg-[#2A9FCB]/20 border border-[#2A9FCB]/30"
              style={{ color: '#2A9FCB' }}
            >
              La Nostra Filosofia
            </p>
            <h2 
              className="text-4xl md:text-5xl lg:text-6xl font-normal mb-6 tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Perché Sceglierci
            </h2>
            <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-white/80">
              In Prime Vision crediamo che ogni paziente meriti <strong className="text-white">tempo, ascolto e attenzione</strong>. 
              Per questo abbiamo rivoluzionato il nostro approccio.
            </p>
          </div>

          {/* Main Value Proposition - Animated Cards */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            {/* Card 1 - Il Paziente al Centro */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#2A9FCB]/50 to-[#1e7ba8]/50 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-700" />
              <div className="relative bg-white/10 backdrop-blur-md p-8 md:p-10 rounded-3xl border border-white/20 hover:border-white/40 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2A9FCB] to-[#1e7ba8] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 
                      className="text-2xl md:text-3xl font-medium text-white"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                      Il Paziente al Centro
                    </h3>
                    <p className="text-[#2A9FCB] text-sm">La nostra priorità assoluta</p>
                  </div>
                </div>
                <p className="text-lg leading-relaxed mb-4 text-white/90">
                  Crediamo che ogni visita debba essere un <strong className="text-white">momento di vera connessione</strong>. 
                  Dedichiamo tutto il tempo necessario ad ascoltare le tue esigenze e costruire insieme il percorso di cura più adatto.
                </p>
                <p className="text-lg leading-relaxed text-white/90">
                  La tua voce merita di essere <span className="text-[#2A9FCB] font-semibold">ascoltata, accolta, compresa</span> e <span className="text-[#2A9FCB] font-semibold">aiutata</span>. Sempre.
                </p>
                {/* Animated Line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2A9FCB] to-[#1e7ba8] rounded-b-3xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </div>
            </div>
            
            {/* Card 2 - Intelligenza Artificiale */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#2A9FCB]/50 to-purple-500/50 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-700" />
              <div className="relative bg-gradient-to-br from-[#2A9FCB] to-[#1e7ba8] p-8 md:p-10 rounded-3xl shadow-2xl hover:-translate-y-2 transition-all duration-500">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 
                      className="text-2xl md:text-3xl font-medium text-white"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                      Intelligenza Artificiale
                    </h3>
                    <p className="text-white/70 text-sm">Al servizio della tua salute</p>
                  </div>
                </div>
                <p className="text-lg leading-relaxed mb-4 text-white/95">
                  Utilizziamo l'<strong className="text-white">Intelligenza Artificiale</strong> per 
                  <strong className="text-white"> liberare tempo prezioso</strong> da dedicare a te.
                </p>
                <p className="text-lg leading-relaxed text-white/90 mb-6">
                  L'IA gestisce la burocrazia, noi ci prendiamo cura di te.
                </p>
                {/* AI Features List */}
                <div className="flex flex-wrap gap-2">
                  {['Chatbot 24/7', 'Referti Smart', 'Promemoria Auto', 'Diagnosi Assistita'].map((feature, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1 bg-white/20 rounded-full text-sm text-white/90 backdrop-blur-sm"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Trust Indicators - Enhanced Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: Clock, title: 'Più Tempo per Te', desc: 'Zero burocrazia, massima attenzione', gradient: 'from-blue-400 to-blue-600' },
              { icon: Heart, title: 'Ascolto Attivo', desc: 'Ogni paziente è unico e importante', gradient: 'from-rose-400 to-rose-600' },
              { icon: Shield, title: 'Tecnologia Avanzata', desc: 'Strumenti diagnostici all\'avanguardia', gradient: 'from-emerald-400 to-emerald-600' },
              { icon: CheckCircle, title: 'Cura Personalizzata', desc: 'Percorsi su misura per te', gradient: 'from-amber-400 to-amber-600' }
            ].map((item, idx) => (
              <div 
                key={idx}
                className="group relative bg-white/5 backdrop-blur-sm p-6 rounded-2xl text-center hover:bg-white/15 transition-all duration-500 border border-white/10 hover:border-white/30 hover:-translate-y-2"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 group-hover:shadow-xl transition-all duration-500`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h4 className="font-semibold text-base md:text-lg mb-2 text-white">{item.title}</h4>
                <p className="text-sm text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chi Siamo Preview Section - Luxury */}
      <section id="chi-siamo" className="py-24 md:py-32 px-6 md:px-12 relative overflow-hidden" style={{background: 'radial-gradient(circle at 50% 50%, #2d7fb7, #45abe9)'}} data-testid="chi-siamo-preview">
        {/* Background texture image */}
        <div className="absolute inset-0 opacity-20" style={{backgroundImage: "url('/bg-blue-1.jpg')", backgroundSize: 'cover', backgroundPosition: 'center'}} />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p 
                className="text-sm font-medium tracking-[0.3em] uppercase mb-4 text-white/70"
              >
                Chi Siamo
              </p>
              <h2 
                className="text-4xl md:text-5xl font-normal mb-8 leading-tight tracking-tight text-white"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                L'eccellenza nella<br />cura della vista
              </h2>
              <p 
                className="text-lg leading-relaxed mb-8 text-white/90"
              >
                Prime Vision nasce dall'unione di competenza medica e innovazione tecnologica. 
                Il nostro approccio pone la <strong className="text-white">prevenzione</strong> al centro di ogni percorso di cura, 
                costruendo relazioni durature basate su ascolto, educazione e responsabilità condivisa.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/chi-siamo" 
                  className="inline-flex items-center justify-center bg-[#FA7314] text-white hover:bg-[#e0650f] rounded-xl px-8 py-4 text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                  data-testid="chi-siamo-link"
                >
                  Scopri la nostra storia
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { icon: Target, title: 'Prevenzione', desc: 'Prima di tutto' },
                { icon: Microscope, title: 'Tecnologia', desc: "All'avanguardia" },
                { icon: Users, title: 'Team', desc: 'Specializzato' },
                { icon: Award, title: 'Eccellenza', desc: 'Nei risultati' }
              ].map((item, idx) => (
                <div 
                  key={idx}
                  className="bg-white p-8 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgba(42,159,203,0.1)] transition-all duration-500 border border-transparent hover:border-[#2A9FCB]/20 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2A9FCB] to-[#1e7ba8] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-semibold text-lg mb-1" style={{color: '#2c4667'}}>{item.title}</h4>
                  <p className="text-sm" style={{color: '#2c4667'}}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Luxury */}
      <section id="servizi" className="py-24 md:py-32 px-6 md:px-12" style={{background: 'linear-gradient(145deg, #e8f4fd 0%, #ffffff 40%, #dcedf8 100%)'}} data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p 
              className="text-sm font-medium tracking-[0.3em] uppercase mb-4"
              style={{ color: '#2A9FCB' }}
            >
              Specializzazioni
            </p>
            <h2 
              className="text-4xl md:text-5xl font-normal mb-6 tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#2c4667' }}
            >
              I Nostri Ambiti di Cura
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#2c4667' }}>
              Esperienza clinica e chirurgica in ogni branca dell'oftalmologia
            </p>
          </div>
          
          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Link
                key={service.id}
                to={service.customLink || `/specialization/${service.id}`}
                className="group relative overflow-hidden rounded-2xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgba(42,159,203,0.15)] transition-all duration-500 border border-gray-100 hover:border-[#2A9FCB]/30"
                data-testid={`service-${service.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <h3 
                    className="text-xl font-medium mb-2 transition-colors"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif", color: service.titleColor || '#2c4667' }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#2c4667' }}>
                    {service.description}
                  </p>
                  <div className="mt-4 flex items-center text-sm font-medium text-[#2A9FCB] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span>Scopri</span>
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          {/* CTA */}
          <div className="text-center mt-16">
            <Link
              to="/prenota"
              className="inline-flex items-center justify-center bg-[#FA7314] hover:bg-[#e0650f] rounded-xl px-8 py-4 text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="book-visit-cta"
              style={{ color: 'white' }}
            >
              <span className="text-white">Prenota una Visita</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Tests Section - Luxury */}
      <section className="py-24 md:py-32 px-6 md:px-12 relative overflow-hidden" style={{background: 'radial-gradient(circle at 50% 50%, #2d7fb7, #45abe9)'}} data-testid="tests-preview-section">
        <div className="absolute inset-0 opacity-15" style={{backgroundImage: "url('/bg-blue-3.jpg')", backgroundSize: 'cover', backgroundPosition: 'center'}} />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <p 
            className="text-sm font-medium tracking-[0.3em] uppercase mb-4 text-white/70"
          >
            Autovalutazione
          </p>
          <h2 
            className="text-4xl md:text-5xl font-normal mb-6 tracking-tight text-white"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Verifica la tua vista
          </h2>
          <p className="text-lg mb-10 leading-relaxed text-white/90">
            Valuta la tua vista comodamente da casa con i nostri test clinici validati. 
            Un primo passo verso la prevenzione.
          </p>
          <Link 
            to="/tests" 
            className="inline-flex items-center justify-center bg-[#FA7314] text-white hover:bg-[#e0650f] rounded-xl px-10 py-4 text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
            data-testid="tests-cta-btn"
          >
            Inizia i Test Gratuiti
          </Link>
        </div>
      </section>

      {/* Packages Section */}
      {/* Abbonamenti CTA Banner */}
      <section id="abbonamenti" className="py-16 px-4" style={{background: 'linear-gradient(135deg, #2c4667 0%, #3d5a80 50%, #4a6fa5 100%)'}} data-testid="abbonamenti-banner">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white">
            Scopri i Nostri Abbonamenti
          </h2>
          <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
            Risparmia fino al 60% con i nostri piani di abbonamento. 
            Visite illimitate, diagnostica completa e chirurgia inclusa.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/abbonamenti" 
              className="px-8 py-4 rounded-xl font-bold text-lg transition-all hover:scale-105 shadow-lg"
              style={{background: 'white', color: '#2c4667'}}
              data-testid="abbonamenti-cta-btn"
            >
              Vedi Tutti gli Abbonamenti →
            </Link>
            <Link 
              to="/abbonamenti/faq" 
              className="px-8 py-4 rounded-xl font-bold text-lg border-2 border-white text-white transition-all hover:bg-[#2A9FCB] hover:border-[#2A9FCB]"
            >
              Domande Frequenti
            </Link>
          </div>
          <p className="mt-6 text-sm text-white/70">
            ⭐ Prenota oggi e inizia il tuo percorso di prevenzione visiva
          </p>
        </div>
      </section>

      {/* Recensioni Section */}
      {recensioni.length > 0 && (
        <section className="py-20 px-4 relative overflow-hidden" style={{background: 'radial-gradient(circle at 50% 50%, #2d7fb7, #45abe9)'}} data-testid="reviews-section">
          <div className="absolute inset-0 opacity-20" style={{backgroundImage: "url('/bg-blue-2.jpg')", backgroundSize: 'cover', backgroundPosition: 'center'}} />
          <div className="relative z-10 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white">
                Cosa Dicono i Nostri Pazienti
              </h2>
              <div className="flex justify-center gap-1 mb-2">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-6 h-6 fill-current" style={{color: '#D4AF37'}} />
                ))}
              </div>
              <p className="text-base text-white/70">La voce dei nostri pazienti</p>
            </div>

            {/* Review Carousel */}
            <div className="relative bg-white rounded-2xl p-8 shadow-lg">
              <Quote className="absolute top-6 left-6 w-10 h-10 opacity-20" style={{color: '#2A9FCB'}} />
              
              <div className="text-center">
                {/* Stars - Now 1-4 */}
                <div className="flex justify-center gap-1 mb-4">
                  {[1,2,3,4].map(i => (
                    <Star 
                      key={i} 
                      className={`w-5 h-5 ${i <= Math.round(recensioni[currentRecensioneIndex]?.rating || 4) ? 'fill-current' : ''}`}
                      style={{color: '#D4AF37'}}
                    />
                  ))}
                </div>
                
                {/* Review Text */}
                {recensioni[currentRecensioneIndex]?.title && (
                  <h3 className="font-semibold text-lg mb-2" style={{color: '#2A9FCB'}}>
                    {recensioni[currentRecensioneIndex]?.title}
                  </h3>
                )}
                {recensioni[currentRecensioneIndex]?.testo && (
                  <p className="text-lg italic mb-6" style={{color: '#2A9FCB'}}>
                    &quot;{recensioni[currentRecensioneIndex]?.testo}&quot;
                  </p>
                )}
                
                {/* Reviewer Info with Verified Badge */}
                <div className="mb-6">
                  <p className="font-semibold" style={{color: '#2A9FCB'}}>
                    {recensioni[currentRecensioneIndex]?.nome}
                  </p>
                  {recensioni[currentRecensioneIndex]?.is_verified_user && (
                    <p className="text-sm flex items-center justify-center gap-1 mt-1" style={{color: '#2A9FCB'}}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                        <path d="m9 12 2 2 4-4"/>
                      </svg>
                      Utente Verificato
                    </p>
                  )}
                  {recensioni[currentRecensioneIndex]?.is_subscribed_user && (
                    <p className="text-sm flex items-center justify-center gap-1 mt-1" style={{color: '#D97706'}}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="5" width="18" height="14" rx="2"/>
                        <path d="M3 10h18"/>
                      </svg>
                      Paziente Abbonato
                    </p>
                  )}
                  {recensioni[currentRecensioneIndex]?.specializzazione && (
                    <p className="text-sm" style={{color: '#9CA3AF'}}>
                      {recensioni[currentRecensioneIndex]?.specializzazione}
                    </p>
                  )}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-center gap-4">
                  <button 
                    onClick={prevRecensione}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                    style={{color: '#2A9FCB'}}
                    data-testid="review-prev-btn"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  
                  {/* Dots */}
                  <div className="flex gap-2">
                    {recensioni.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentRecensioneIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          idx === currentRecensioneIndex ? 'w-6' : ''
                        }`}
                        style={{
                          background: idx === currentRecensioneIndex ? '#2A9FCB' : '#E5E7EB'
                        }}
                      />
                    ))}
                  </div>
                  
                  <button 
                    onClick={nextRecensione}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                    style={{color: '#2A9FCB'}}
                    data-testid="review-next-btn"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden" style={{background: 'linear-gradient(135deg, #1a3a5c 0%, #2c4667 50%, #1e4d6b 100%)'}} data-testid="cta-section">
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">
            Prenditi Cura dei Tuoi Occhi Oggi
          </h2>
          <p className="text-base lg:text-lg mb-8 text-white/80">
            Prenota la tua visita o scopri i nostri pacchetti di abbonamento
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking" className="btn-primary" data-testid="cta-book-btn">
              Prenota Visita
            </Link>
            <Link to="/auth" className="btn-secondary" data-testid="cta-signup-btn">
              Registrati Ora
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
